import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormBuilderService } from '../../../core/services/form-builder.service';
import {
  EIremboFormlyFieldSubTypes,
  EIremboFormlyFieldTypes,
  EIremboFormlyFormStateKeys,
} from '@irembo-andela/irembogov3-common';
import { GetEnumToLabelAndValueOptions } from '../../../core/utils/form-builder-field-comparator-dropdown-options.utils';
import {
  EFormBuilderDisplayConditionComparator,
  FormBuilderFieldsWithDataSet,
  FormBuilderFieldsWithOptions,
} from '../../../core/models/form-builder-display-comparator.enum';
import {
  IBasicFieldLabelAndDetails,
  IBasicLabelValuePair,
} from '../../../core/models/basic-label-value-pair.model';
import { TFormlyFieldKey } from '../../../core/models/formly-key.type';
import { ServiceComparatorOptions } from '../../../core/constants/comparator-options.constants';
import { EServiceChangeRequestComparator } from '../../../core/models/service-change-request-comparator.enum';
import {
  getCurrentServiceFormFields,
  getAllServiceFormFieldsIncludingSelf,
  fetchedDataFieldsFormFieldLabelAndDetails,
  buildComparatorOptions,
} from '../../../core/utils/form-fields-formly-configurations/conditional-settings-configurations';
import { getExpressionPropsForType } from '../models/expression-properties.registry';

// ─── Expression target properties ───────────────────────────────────────

export interface IExpressionTarget {
  label: string;
  value: string;
  type: 'boolean' | 'value';
  category: string;
  actionOptions?: { label: string; value: string }[];
  valueType?: 'string' | 'number';
}

export const EXPRESSION_TARGETS: IExpressionTarget[] = [
  // ── Boolean targets (behavior) ──
  {
    label: 'Hide / Show',
    value: 'hide',
    type: 'boolean',
    category: 'Behavior',
    actionOptions: [
      { label: 'Show this field when', value: 'falseWhen' },
      { label: 'Hide this field when', value: 'trueWhen' },
    ],
  },
  {
    label: 'Required',
    value: 'props.required',
    type: 'boolean',
    category: 'Behavior',
    actionOptions: [
      { label: 'Require when', value: 'trueWhen' },
      { label: 'Optional when', value: 'falseWhen' },
    ],
  },
  {
    label: 'Disabled',
    value: 'props.disabled',
    type: 'boolean',
    category: 'Behavior',
    actionOptions: [
      { label: 'Disable when', value: 'trueWhen' },
      { label: 'Enable when', value: 'falseWhen' },
    ],
  },
  {
    label: 'Read Only',
    value: 'props.readonly',
    type: 'boolean',
    category: 'Behavior',
    actionOptions: [
      { label: 'Read-only when', value: 'trueWhen' },
      { label: 'Editable when', value: 'falseWhen' },
    ],
  },
  {
    label: 'Hide Field (CSS)',
    value: 'props.hideField',
    type: 'boolean',
    category: 'Behavior',
    actionOptions: [
      { label: 'Hide (CSS) when', value: 'trueWhen' },
      { label: 'Visible when', value: 'falseWhen' },
    ],
  },
  {
    label: 'Multiple Selection',
    value: 'props.multiple',
    type: 'boolean',
    category: 'Behavior',
    actionOptions: [
      { label: 'Allow multiple when', value: 'trueWhen' },
      { label: 'Single select when', value: 'falseWhen' },
    ],
  },
  // ── Value targets (appearance) ──
  {
    label: 'Label',
    value: 'props.label',
    type: 'value',
    category: 'Appearance',
    valueType: 'string',
  },
  {
    label: 'Placeholder',
    value: 'props.placeholder',
    type: 'value',
    category: 'Appearance',
    valueType: 'string',
  },
  {
    label: 'Description',
    value: 'props.description',
    type: 'value',
    category: 'Appearance',
    valueType: 'string',
  },
  {
    label: 'Tooltip',
    value: 'props.tooltip',
    type: 'value',
    category: 'Appearance',
    valueType: 'string',
  },
  {
    label: 'Hint',
    value: 'props.hint',
    type: 'value',
    category: 'Appearance',
    valueType: 'string',
  },
  // ── Value targets (constraints) ──
  {
    label: 'Min Value',
    value: 'props.min',
    type: 'value',
    category: 'Constraints',
    valueType: 'number',
  },
  {
    label: 'Max Value',
    value: 'props.max',
    type: 'value',
    category: 'Constraints',
    valueType: 'number',
  },
  {
    label: 'Min Length',
    value: 'props.minLength',
    type: 'value',
    category: 'Constraints',
    valueType: 'number',
  },
  {
    label: 'Max Length',
    value: 'props.maxLength',
    type: 'value',
    category: 'Constraints',
    valueType: 'number',
  },
  {
    label: 'Pattern',
    value: 'props.pattern',
    type: 'value',
    category: 'Constraints',
    valueType: 'string',
  },
  {
    label: 'Min File Size',
    value: 'props.minimumUploadSize',
    type: 'value',
    category: 'Constraints',
    valueType: 'number',
  },
  {
    label: 'Max File Size',
    value: 'props.maximumUploadSize',
    type: 'value',
    category: 'Constraints',
    valueType: 'number',
  },
  {
    label: 'Min Selection',
    value: 'props.minSelection',
    type: 'value',
    category: 'Constraints',
    valueType: 'number',
  },
  {
    label: 'Max Selection',
    value: 'props.maxSelection',
    type: 'value',
    category: 'Constraints',
    valueType: 'number',
  },
  // ── Value targets (validation messages) ──
  {
    label: 'Required Message',
    value: 'validation.messages.required',
    type: 'value',
    category: 'Messages',
    valueType: 'string',
  },
  {
    label: 'Min Length Message',
    value: 'validation.messages.minLength',
    type: 'value',
    category: 'Messages',
    valueType: 'string',
  },
  {
    label: 'Max Length Message',
    value: 'validation.messages.maxLength',
    type: 'value',
    category: 'Messages',
    valueType: 'string',
  },
  {
    label: 'Min Value Message',
    value: 'validation.messages.min',
    type: 'value',
    category: 'Messages',
    valueType: 'string',
  },
  {
    label: 'Max Value Message',
    value: 'validation.messages.max',
    type: 'value',
    category: 'Messages',
    valueType: 'string',
  },
  {
    label: 'Pattern Message',
    value: 'validation.messages.pattern',
    type: 'value',
    category: 'Messages',
    valueType: 'string',
  },
];

/**
 * Builds a dynamic list of expression targets by merging the base
 * EXPRESSION_TARGETS with type-specific properties from the registry.
 */
export function getExpressionTargetsForField(
  fieldType?: string,
  fieldKey?: string
): IExpressionTarget[] {
  if (!fieldType) return EXPRESSION_TARGETS;

  const targets = [...EXPRESSION_TARGETS];
  const existingKeys = new Set(targets.map(t => t.value));
  const typeProps = getExpressionPropsForType(fieldType, fieldKey || '');

  for (const prop of typeProps) {
    if (existingKeys.has(prop.key)) continue;
    existingKeys.add(prop.key);

    if (prop.valueType === 'boolean') {
      targets.push({
        label: prop.label,
        value: prop.key,
        type: 'boolean',
        category:
          prop.category === 'visibility' || prop.category === 'state'
            ? 'Behavior'
            : 'Constraints',
        actionOptions: [
          { label: `Enable ${prop.label} when`, value: 'trueWhen' },
          { label: `Disable ${prop.label} when`, value: 'falseWhen' },
        ],
      });
    } else {
      targets.push({
        label: prop.label,
        value: prop.key,
        type: 'value',
        category:
          prop.category === 'appearance'
            ? 'Appearance'
            : prop.category === 'validation'
            ? 'Constraints'
            : 'Data',
        valueType: prop.valueType === 'number' ? 'number' : 'string',
      });
    }
  }

  return targets;
}

// ─── Visual model interfaces ────────────────────────────────────────────

export interface IExpressionBuilderModel {
  rules: IExpressionBuilderRule[];
}

export interface IExpressionBuilderRule {
  targetProperty: string;
  action: 'trueWhen' | 'falseWhen';
  valueWhenTrue?: string;
  defaultValue?: string;
  conditionGroups: IConditionGroup[];
}

export interface IConditionGroup {
  groupOperator?: 'AND' | 'OR';
  conditions: IExpressionBuilderCondition[];
}

export interface IExpressionBuilderCondition {
  operator?: 'AND' | 'OR';
  comparedto: 'formField' | 'formState';
  formField?: any;
  formFieldProperty?: { value: string; label: string; type: string };
  formStateValue?: {
    formStateKey?: string;
    fieldKey?: string;
    valuePath?: string;
  };
  comparator?: string;
  fieldvalue?: any;
  valueAsNumber?: boolean;
  // Path overrides: when set, the generator uses these instead of formField's
  // rootModelPath/fieldRefPath. This lets the dropdown keep the original field
  // reference (for matching) while preserving the original expression form.
  rootModelPathOverride?: string;
  fieldRefPathOverride?: string;
}

// ─── Formly form configuration ──────────────────────────────────────────

export const getExpressionBuilderFormConfig = (
  formBuilderServiceInstance: FormBuilderService,
  targets?: IExpressionTarget[]
): FormlyFieldConfig => {
  const effectiveTargets = targets || EXPRESSION_TARGETS;
  return {
    type: EIremboFormlyFieldTypes['formly-group'],
    props: {
      hideHeader: true,
      dragDisabled: true,
    },
    fieldGroup: [
      {
        key: 'rules',
        type: EIremboFormlyFieldTypes.customrepeater,
        className: 'form-builder-display-rules',
        props: {
          hideHeader: true,
          required: false,
          addText: 'Add Expression Rule',
          dragDisabled: true,
          template: 'displayRules',
          title: 'Expression',
        },
        fieldArray: {
          className: 'form-builder-display-rules-group',
          type: EIremboFormlyFieldTypes['formly-group'],
          props: {
            hideHeader: true,
            required: false,
            dragDisabled: true,
          },
          fieldGroup: [
            // Target property selector
            {
              key: 'targetProperty',
              type: EIremboFormlyFieldTypes.customdropdown,
              props: {
                dragDisabled: true,
                label: 'Set property',
                placeholder: 'Select property to control',
                required: true,
                options: effectiveTargets.map(t => ({
                  label: `${t.label} (${t.category})`,
                  value: t.value,
                })),
                groupBy: (item: any) => {
                  const target = effectiveTargets.find(
                    t => t.value === item.value
                  );
                  return target?.category || 'Other';
                },
                bindLabel: 'label',
                bindValue: 'value',
              },
            },
            // Action selector (dynamic based on target)
            {
              key: 'action',
              type: EIremboFormlyFieldTypes.customdropdown,
              props: {
                dragDisabled: true,
                label: 'Behavior',
                placeholder: 'Select behavior',
                required: true,
                bindLabel: 'label',
                bindValue: 'value',
                options: [],
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const targetProp = field?.parent?.model?.targetProperty;
                  if (!targetProp) return true;
                  const target = effectiveTargets.find(
                    t => t.value === targetProp
                  );
                  return target?.type !== 'boolean';
                },
                'props.options': (field: FormlyFieldConfig) => {
                  const target = effectiveTargets.find(
                    t => t.value === field?.parent?.model?.targetProperty
                  );
                  return target?.actionOptions || [];
                },
              },
            },
            // Value when conditions met (only for value targets)
            {
              key: 'valueWhenTrue',
              type: EIremboFormlyFieldTypes.input,
              props: {
                dragDisabled: true,
                label: 'Value when conditions met',
                placeholder: 'Enter value',
                required: true,
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const targetProp = field?.parent?.model?.targetProperty;
                  if (!targetProp) return true;
                  const target = effectiveTargets.find(
                    t => t.value === targetProp
                  );
                  return target?.type !== 'value';
                },
              },
            },
            // Default value (only for value targets)
            {
              key: 'defaultValue',
              type: EIremboFormlyFieldTypes.input,
              props: {
                dragDisabled: true,
                label: 'Default value (when conditions not met)',
                placeholder: 'Enter default value',
                required: true,
              },
              expressions: {
                hide: (field: FormlyFieldConfig) => {
                  const targetProp = field?.parent?.model?.targetProperty;
                  if (!targetProp) return true;
                  const target = effectiveTargets.find(
                    t => t.value === targetProp
                  );
                  return target?.type !== 'value';
                },
              },
            },
            // Condition Branches (bracketed groups)
            {
              key: 'conditionGroups',
              type: EIremboFormlyFieldTypes.customrepeater,
              className: 'form-builder-display-rules fb-condition-groups',
              props: {
                hideHeader: true,
                required: false,
                addText: 'Add another branch (OR / AND)',
                dragDisabled: true,
                template: 'displayRules',
                title: 'When',
              },
              expressions: {
                hide: (field: FormlyFieldConfig) =>
                  !field?.parent?.model?.targetProperty,
              },
              fieldArray: {
                className:
                  'form-builder-display-rules-group fb-condition-group-item',
                type: EIremboFormlyFieldTypes['formly-group'],
                props: {
                  hideHeader: true,
                  required: false,
                  dragDisabled: true,
                },
                fieldGroup: [
                  // Branch connector (AND/OR) — shown between branches
                  {
                    key: 'groupOperator',
                    type: EIremboFormlyFieldTypes.customdropdown,
                    className: 'fb-group-operator',
                    props: {
                      hideHeader: true,
                      label: 'Connect to previous branch with',
                      placeholder: 'Select connector...',
                      required: false,
                      options: [
                        {
                          value: 'OR',
                          label: 'OR — at least one branch must match',
                        },
                        {
                          value: 'AND',
                          label: 'AND — both branches must match',
                        },
                      ],
                      bindLabel: 'label',
                      bindValue: 'value',
                    },
                    expressions: {
                      'props.required': (field: FormlyFieldConfig) =>
                        field?.parent?.key !== '0',
                      hide: (field: FormlyFieldConfig) =>
                        field?.parent?.key === '0',
                    },
                  },
                  // Conditions within this branch
                  {
                    key: 'conditions',
                    type: EIremboFormlyFieldTypes.customrepeater,
                    className:
                      'form-builder-display-rules fb-conditions-in-group',
                    props: {
                      hideHeader: true,
                      required: false,
                      addText: 'Add Condition',
                      dragDisabled: true,
                      template: 'displayRules',
                      title: 'Condition',
                    },
                    fieldArray: {
                      className: 'form-builder-display-rules-group',
                      type: EIremboFormlyFieldTypes['formly-group'],
                      defaultValue: {},
                      props: {
                        hideHeader: true,
                        required: false,
                        dragDisabled: true,
                      },
                      fieldGroup: buildConditionFields(
                        formBuilderServiceInstance
                      ),
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  };
};

// ─── Condition fields (replicates conditional settings pattern) ─────────

function buildConditionFields(
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig[] {
  return [
    // AND/OR operator (hidden for first condition, locked for 3rd+ to match branch)
    {
      key: 'operator',
      type: EIremboFormlyFieldTypes.customdropdown,
      props: {
        hideHeader: true,
        placeholder: 'AND or OR',
        required: false,
        options: GetEnumToLabelAndValueOptions(
          EFormBuilderDisplayConditionComparator
        ),
        bindLabel: 'label',
        bindValue: 'value',
      },
      expressions: {
        'props.required': (field: FormlyFieldConfig) =>
          field?.parent?.key !== '0',
        hide: (field: FormlyFieldConfig) => field?.parent?.key === '0',
        'props.readonly': (field: FormlyFieldConfig) => {
          const idx = parseInt(String(field?.parent?.key || '0'), 10);
          return idx > 1;
        },
        'props.description': (field: FormlyFieldConfig) => {
          const idx = parseInt(String(field?.parent?.key || '0'), 10);
          return idx > 1
            ? 'All conditions in a branch must use the same connector (AND/OR). To change it, update the second condition above.'
            : '';
        },
      },
      hooks: {
        // Sync 3rd+ conditions to match the branch's first operator.
        // Uses FormControl directly because model may be undefined for new items.
        onInit: (field: FormlyFieldConfig) => {
          const idx = parseInt(String(field?.parent?.key || '0'), 10);
          if (idx <= 1) return;
          const branchOp = getBranchOperator(field);
          if (branchOp && field?.formControl) {
            field.formControl.setValue(branchOp);
            if (field.model) field.model.operator = branchOp;
          }
        },
      },
    },

    // Compare against (Form Field or Form State)
    {
      key: 'comparedto',
      type: EIremboFormlyFieldTypes.customdropdown,
      props: {
        label: 'Compare against',
        required: true,
        placeholder: 'Select what to compare',
        bindLabel: 'label',
        bindValue: 'value',
        options: [
          { value: 'formField', label: 'Form Field' },
          { value: 'formState', label: 'Form State' },
        ],
      },
    },

    // Form field selector
    {
      key: 'formField',
      type: EIremboFormlyFieldTypes.customdropdown,
      props: {
        label: 'Form field',
        required: true,
        placeholder: 'Select form field',
        bindLabel: 'label',
        bindValue: 'value',
        options: getAllServiceFormFieldsIncludingSelf(
          formBuilderServiceInstance
        ),
        compareWith: (o1: any, o2: any) => o1?.value === o2?.value,
        change: (field: any) => {
          const form = field.form;
          form?.get('formFieldProperty')?.reset();
          form?.get('comparator')?.reset();
          form?.get('fieldvalue')?.reset();
        },
      },
      expressions: {
        hide: (field: FormlyFieldConfig) =>
          field?.parent?.model?.comparedto !== 'formField',
        'props.required': '!field.hide',
      },
    },

    // Field property to compare (value, validity, disabled, readonly)
    {
      key: 'formFieldProperty',
      type: EIremboFormlyFieldTypes.customdropdown,
      props: {
        label: 'Field property',
        placeholder: 'Select property to compare',
        bindLabel: 'label',
        bindValue: '',
        options: [
          { value: 'fieldvalue', label: 'Field value', type: 'unknown' },
          {
            value: 'hasValue',
            label: 'Has any value (exists)',
            type: 'boolean',
          },
          { value: 'validity', label: "Field's Validity", type: 'boolean' },
          { value: 'disabled', label: 'Disabled', type: 'boolean' },
          { value: 'readonly', label: 'Readonly', type: 'boolean' },
        ],
      },
      expressions: {
        hide: (field: FormlyFieldConfig) =>
          field?.model?.comparedto !== 'formField',
      },
    },

    // Form State value path group
    {
      key: 'formStateValue',
      type: EIremboFormlyFieldTypes['formly-group'],
      props: { hideHeader: true, required: false },
      fieldGroup: [
        {
          key: 'formStateKey',
          type: EIremboFormlyFieldTypes.select,
          props: {
            hideHeader: true,
            placeholder: 'Select form state key',
            required: true,
            bindValue: 'value',
            options: [
              {
                label: 'Fetched Data',
                value: EIremboFormlyFormStateKeys.FETCHED_DATA,
              },
              {
                label: 'Application State',
                value: EIremboFormlyFormStateKeys.APPLICATION_STATE,
              },
              {
                label: 'Applicant Type',
                value: EIremboFormlyFormStateKeys.APPLICANT_TYPE,
              },
              {
                label: 'Application Locale',
                value: EIremboFormlyFormStateKeys.LOCALE,
              },
            ],
          },
        },
        {
          key: 'fieldKey',
          type: EIremboFormlyFieldTypes.customdropdown,
          props: {
            hideHeader: true,
            placeholder: 'Select the field key in formState data',
            required: true,
            bindLabel: 'label',
            bindValue: 'value',
            options: fetchedDataFieldsFormFieldLabelAndDetails(
              formBuilderServiceInstance
            ),
          },
          expressions: {
            hide: (field: FormlyFieldConfig) =>
              !(
                field?.parent?.parent?.model?.formStateValue?.formStateKey ===
                EIremboFormlyFormStateKeys.FETCHED_DATA
              ),
          },
        },
        {
          key: 'valuePath',
          type: EIremboFormlyFieldTypes.input,
          props: {
            hideHeader: true,
            placeholder: 'Value path',
            required: true,
            description: 'e.g. `sex` in {sex: "MALE"} or `users?.[0].sex`',
          },
        },
      ],
      expressions: {
        hide: (field: FormlyFieldConfig) =>
          field?.parent?.model?.comparedto !== 'formState',
        'props.required': '!field.hide',
      },
    },

    // Comparator (==, !=, >, <, etc.)
    {
      key: 'comparator',
      type: EIremboFormlyFieldTypes.customdropdown,
      props: {
        label: 'Comparator',
        placeholder: 'Select comparator',
        bindLabel: 'label',
        bindValue: 'value',
        options: ServiceComparatorOptions,
      },
      expressions: {
        'props.options': buildComparatorOptions,
        'model.comparator': 'model?.comparator',
        'props.required': '!field.hide',
        hide: (field: FormlyFieldConfig) => {
          if (field?.model?.comparedto == null) return true;
          if (field?.model?.comparedto === 'formState') return false;
          return (
            field?.model?.formFieldProperty?.type === 'boolean' ||
            ['checkbox'].includes(field?.model?.formField?.fieldType)
          );
        },
      },
    },

    // Field value - text input
    {
      key: 'fieldvalue',
      type: EIremboFormlyFieldTypes.input,
      props: {
        label: 'Value',
        required: false,
        placeholder: 'Enter value to compare',
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => {
          if (
            field.model?.formFieldProperty?.type === 'boolean' ||
            field.model?.comparator === 'BOOLEAN'
          ) {
            return true;
          }
          // For formState: show the text input (unless BOOLEAN, caught above)
          if (field.model?.comparedto === 'formState') return false;
          // For formField: hide if field has predefined options
          if (field.model?.comparedto !== 'formField') return true;
          return (
            FormBuilderFieldsWithOptions.includes(
              field?.model?.formField?.fieldType
            ) ||
            FormBuilderFieldsWithDataSet.includes(
              field?.model?.formField?.fieldType
            )
          );
        },
        'props.required': '!field.hide',
      },
    },

    // Field value - boolean (true/false) dropdown
    {
      key: 'fieldvalue',
      type: EIremboFormlyFieldTypes.customdropdown,
      props: {
        label: 'Select value',
        required: false,
        placeholder: 'Select value',
        bindLabel: 'label',
        bindValue: 'value',
        options: [
          { value: true, label: 'True' },
          { value: false, label: 'False' },
        ],
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => {
          if (
            field.model?.comparedto === 'formState' &&
            field.model?.comparator !== 'BOOLEAN'
          ) {
            return true;
          }
          if (
            field.model?.formFieldProperty?.type === 'boolean' ||
            ['checkbox'].includes(field?.model?.formField?.fieldType) ||
            field.model?.comparator === 'BOOLEAN'
          ) {
            return false;
          }
          return true;
        },
        'props.required': '!field.hide',
        'props.options': (field: FormlyFieldConfig) => {
          if (field.model?.formFieldProperty?.value === 'validity') {
            return [
              { value: true, label: 'Is valid' },
              { value: false, label: 'Not valid' },
            ];
          }
          if (field.model?.formFieldProperty?.value === 'hasValue') {
            return [
              { value: true, label: 'Yes — has a value' },
              { value: false, label: 'No — is empty' },
            ];
          }
          return [
            { value: true, label: 'True' },
            { value: false, label: 'False' },
          ];
        },
      },
    },

    // Field value - dropdown options (for fields with predefined options)
    {
      key: 'fieldvalue',
      type: EIremboFormlyFieldTypes.customdropdown,
      props: {
        placeholder: 'Select a value',
        label: 'Select a value',
        options: [],
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => {
          if (field?.model?.comparedto !== 'formField') return true;
          const { formField } = field?.model || {};
          const { fieldType } = formField || {};
          if (
            field?.model?.formFieldProperty?.type === 'boolean' ||
            ['checkbox'].includes(field?.model?.formField?.fieldType)
          ) {
            return true;
          }
          if (
            field?.model?.formField?.fieldSubType ===
              EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH ||
            field?.model?.formField?.fieldSubType ===
              EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
          ) {
            return true;
          }
          if (
            fieldType === EIremboFormlyFieldTypes.customdropdownpaginated ||
            [
              EIremboFormlyFieldTypes.customdropdown,
              EIremboFormlyFieldTypes.radio,
              EIremboFormlyFieldTypes.multicheckbox,
              EIremboFormlyFieldTypes.select,
            ].includes(fieldType)
          ) {
            return false;
          }
          return true;
        },
        'props.required': '!field?.hide',
        'props.bindLabel': (field: FormlyFieldConfig) =>
          formBuilderServiceInstance.getFormFieldOptionLabelAndValue({
            compareField: field?.model?.formField,
          })?.props?.['bindLabel'] ?? 'label',
        'props.bindValue': (field: FormlyFieldConfig) =>
          formBuilderServiceInstance.getFormFieldOptionLabelAndValue({
            compareField: field?.model?.formField,
          })?.props?.['bindValue'] ?? 'value',
        'props.options': (field: FormlyFieldConfig) => {
          if (
            FormBuilderFieldsWithOptions.includes(
              field?.model?.formField?.fieldType
            ) &&
            field?.model?.formField?.fieldSubType !==
              EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH &&
            field?.model?.formField?.fieldSubType !==
              EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
          ) {
            return (
              formBuilderServiceInstance.getFormFieldOptionLabelAndValue({
                compareField: field?.model?.formField,
              })?.props?.options || []
            );
          }
          return [];
        },
      },
    },

    // Field value - data-fetch/dataset dropdown
    {
      key: 'fieldvalue',
      type: 'input',
      props: {
        placeholder: 'Select a value',
        label: 'Select a value',
        options: [],
        useBaseUrl: (field: FormlyFieldConfig) =>
          field?.model?.formField?.fieldType ===
            EIremboFormlyFieldTypes.customdropdownpaginated ||
          field?.model?.formField?.fieldSubType ===
            EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH ||
          field?.model?.formField?.fieldSubType ===
            EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET,
        subType: (field: FormlyFieldConfig) =>
          field?.model?.formField?.fieldSubType,
      },
      expressions: {
        type: (field: FormlyFieldConfig) => {
          if (
            field?.model?.formField?.fieldSubType ===
              EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH ||
            field?.model?.formField?.fieldSubType ===
              EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
          ) {
            return EIremboFormlyFieldTypes.customdropdown;
          }
          if (
            field?.model?.formField?.fieldType ===
            EIremboFormlyFieldTypes.customdropdownpaginated
          ) {
            return EIremboFormlyFieldTypes.customdropdownpaginated;
          }
          return EIremboFormlyFieldTypes.customdropdown;
        },
        hide: (field: FormlyFieldConfig) => {
          if (field?.model?.comparedto !== 'formField') return true;
          if (
            field?.model?.formFieldProperty?.type === 'boolean' ||
            ['checkbox'].includes(field?.model?.formField?.fieldType)
          ) {
            return true;
          }
          if (
            field?.model?.formField?.fieldSubType ===
              EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH ||
            field?.model?.formField?.fieldSubType ===
              EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
          ) {
            return false;
          }
          return true;
        },
        'props.required': '!field?.hide',
        'props.dataset': (field: FormlyFieldConfig) => {
          const { formField } = field?.model || {};
          if (!formField) return;
          if (
            formField.fieldType ===
              EIremboFormlyFieldTypes.customdropdownpaginated ||
            formField.fieldSubType ===
              EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH ||
            formField.fieldSubType ===
              EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
          ) {
            return formBuilderServiceInstance.getFormFieldOptionLabelAndValue({
              compareField: field?.model?.formField,
            })?.props?.['dataset'];
          }
        },
        'props.bindLabel': (field: FormlyFieldConfig) =>
          formBuilderServiceInstance.getFormFieldOptionLabelAndValue({
            compareField: field?.model?.formField,
          })?.props?.['bindLabel'] ?? 'label',
        'props.bindValue': (field: FormlyFieldConfig) =>
          formBuilderServiceInstance.getFormFieldOptionLabelAndValue({
            compareField: field?.model?.formField,
          })?.props?.['bindValue'] ?? 'value',
        'props.code': (field: FormlyFieldConfig) => {
          if (
            field?.model?.formField?.fieldSubType ===
            EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH
          ) {
            return formBuilderServiceInstance.getFormFieldOptionLabelAndValue({
              compareField: field?.model?.formField,
            })?.props?.['code'];
          }
        },
      },
    },

    // Value as number checkbox
    {
      key: 'valueAsNumber',
      type: EIremboFormlyFieldTypes.checkbox,
      defaultValue: false,
      props: {
        hideHeader: true,
        label: 'Value as Number',
      },
      expressions: {
        hide: (field: FormlyFieldConfig) =>
          field?.model?.comparedto !== 'formState' ||
          field.model?.comparator === 'BOOLEAN',
      },
    },
  ];
}

/** Find the first operator set by any condition in this branch (via repeater fieldGroup). */
function getBranchOperator(field: FormlyFieldConfig): string | null {
  // field.parent = formly-group(idx), field.parent.parent = conditions repeater
  const conditionItems = field?.parent?.parent?.fieldGroup;
  if (conditionItems?.length) {
    for (const item of conditionItems) {
      if (item?.model?.operator) return item.model.operator;
    }
  }
  return null;
}

// ─── Expression generator ───────────────────────────────────────────────

const OPERATOR_MAP: Record<string, string> = {
  AND: '&&',
  OR: '||',
};

const COMPARATOR_MAP: Record<string, string> = {
  [EServiceChangeRequestComparator.EQ]: '===',
  [EServiceChangeRequestComparator.GT]: '>',
  [EServiceChangeRequestComparator.GTE]: '>=',
  [EServiceChangeRequestComparator.LT]: '<',
  [EServiceChangeRequestComparator.LTE]: '<=',
  [EServiceChangeRequestComparator.NEQ]: '!==',
  [EServiceChangeRequestComparator.CONTAINS]: 'includes',
};

export const buildExpressionsFromVisualModel = (
  model: IExpressionBuilderModel,
  targets?: IExpressionTarget[]
): Record<string, string> => {
  const effectiveTargets = targets || EXPRESSION_TARGETS;
  const result: Record<string, string> = {};

  if (!model?.rules) return result;

  model.rules.forEach((rule: IExpressionBuilderRule) => {
    if (!rule.targetProperty || !rule.conditionGroups?.length) return;

    const groupExprs: string[] = [];
    const groupOps: string[] = [];

    rule.conditionGroups.forEach((group: IConditionGroup) => {
      if (!group.conditions?.length) return;

      // Enforce a single operator per branch: use the first non-null operator
      // for ALL conditions in the branch. This prevents precedence bugs from
      // mixing AND/OR (e.g. "A && B || C" being misinterpreted).
      const validConditions = group.conditions.filter(c => c != null);
      if (!validConditions.length) return;

      const branchOperator =
        validConditions.find(c => c.operator)?.operator || 'AND';

      const conditionParts: string[] = [];

      validConditions.forEach(
        (condition: IExpressionBuilderCondition, idx: number) => {
          const condExpr = buildSingleConditionExpr(condition);
          if (!condExpr) return;

          if (idx > 0 || condition.operator) {
            // For all conditions after the first, use the branch's unified operator
            if (conditionParts.length > 0) {
              conditionParts.push(OPERATOR_MAP[branchOperator]);
            }
          }
          conditionParts.push(condExpr);
        }
      );

      const groupExpr = conditionParts.join(' ');
      if (!groupExpr) return;

      if (group.groupOperator) {
        groupOps.push(OPERATOR_MAP[group.groupOperator]);
      }
      // Wrap multi-condition groups in brackets
      groupExprs.push(
        group.conditions.length > 1 ? `(${groupExpr})` : groupExpr
      );
    });

    if (groupExprs.length === 0) return;

    // Join groups: if multiple groups, interleave group operators
    // Always wrap each group in parentheses to enforce left-to-right evaluation
    // and prevent precedence issues when mixing AND/OR between groups.
    let conditionExpr: string;
    if (groupExprs.length === 1) {
      conditionExpr = groupExprs[0];
    } else {
      // Wrap each group expression in parens for safety
      const wrapped = groupExprs.map(g =>
        g.startsWith('(') && g.endsWith(')') ? g : `(${g})`
      );
      const parts: string[] = [wrapped[0]];
      for (let i = 1; i < wrapped.length; i++) {
        parts.push(groupOps[i - 1] || '||');
        parts.push(wrapped[i]);
      }
      conditionExpr = parts.join(' ');
    }

    // Determine expression based on target type
    const target = effectiveTargets.find(t => t.value === rule.targetProperty);

    if (target?.type === 'value') {
      const whenTrue = formatRuleValue(rule.valueWhenTrue, target.valueType);
      const whenFalse = formatRuleValue(rule.defaultValue, target.valueType);
      result[
        rule.targetProperty
      ] = `${conditionExpr} ? ${whenTrue} : ${whenFalse}`;
    } else {
      const finalExpr =
        rule.action === 'falseWhen' ? `!(${conditionExpr})` : conditionExpr;
      result[rule.targetProperty] = finalExpr;
    }
  });

  return result;
};

function buildSingleConditionExpr(
  condition: IExpressionBuilderCondition
): string | null {
  const {
    formField,
    formFieldProperty,
    comparator,
    fieldvalue,
    formStateValue,
    valueAsNumber,
    comparedto,
    rootModelPathOverride,
    fieldRefPathOverride,
  } = condition;

  if (comparedto === 'formState') {
    if (!formStateValue?.formStateKey || !formStateValue?.valuePath)
      return null;

    let pathList = `formState?.${formStateValue.formStateKey}`;
    if (formStateValue.fieldKey) {
      pathList = `${pathList}?.${formStateValue.fieldKey}`;
    }
    pathList = `${pathList}?.${formStateValue.valuePath}`;

    let valueToCompare: any = fieldvalue;
    if (valueAsNumber) {
      valueToCompare = Number(valueToCompare);
    } else {
      valueToCompare = formatValue(fieldvalue, formField);
    }

    let formattedComparator = '===';
    if (comparator) {
      formattedComparator = COMPARATOR_MAP[comparator] ?? '===';
    }

    // CONTAINS uses .includes() method syntax
    if (comparator === EServiceChangeRequestComparator.CONTAINS) {
      return `${pathList}?.includes(${valueToCompare})`;
    }

    return `${pathList} ${formattedComparator} ${valueToCompare}`;
  }

  if (comparedto === 'formField') {
    // Self-reference expressions (e.g., field?.hide, !field?.hide)
    if (formField?.key === '__self__') {
      const propPath = formFieldProperty?.value || 'hide';
      const prefix = fieldvalue === false ? '!' : '';
      return `${prefix}field?.${propPath}`;
    }

    const rootModelPath = rootModelPathOverride || formField?.rootModelPath;
    let fieldRefPath = fieldRefPathOverride || formField?.fieldRefPath;

    if (
      formField?.fieldSubType &&
      [
        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET,
        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET_PAGINATED,
      ].includes(formField.fieldSubType as EIremboFormlyFieldSubTypes) &&
      !fieldRefPath?.endsWith('?.value')
    ) {
      fieldRefPath = `${fieldRefPath}?.value`;
    }

    const fullPath = `${rootModelPath}${
      fieldRefPath ? '?.' + fieldRefPath : ''
    }`;

    // Has any value (null/undefined check): use != null for correctness with 0, '', false
    if (formFieldProperty?.value === 'hasValue') {
      return fieldvalue ? `${fullPath} != null` : `${fullPath} == null`;
    }

    const valueToCompare = formatValue(fieldvalue, formField);

    if (formFieldProperty?.type !== 'boolean') {
      // Skip incomplete conditions (missing comparator or value)
      if (!comparator && (fieldvalue == null || fieldvalue === '')) return null;
      const formattedComparator = comparator
        ? COMPARATOR_MAP[comparator]
        : '===';
      if (comparator === EServiceChangeRequestComparator.CONTAINS) {
        return `${fullPath}?.includes(${valueToCompare})`;
      } else {
        return `${fullPath} ${formattedComparator} ${valueToCompare}`;
      }
    } else {
      const parts = (fieldRefPath || '').split('?.');
      if (formFieldProperty?.value === 'validity') {
        const chain = parts
          .map((part: string) => `get?.('${part}')`)
          .join('?.');
        const validity = fieldvalue ? 'valid' : 'invalid';
        return `field?.form?.${chain}?.${validity}`;
      } else {
        const chain = parts.reduce((acc: string, part: string) => {
          return `${acc}?.fieldGroup?.find(f => f.key === '${part}')`;
        }, 'field?.parent?.parent?.parent');
        return `${chain}?.props?.${formFieldProperty?.value} === ${fieldvalue}`;
      }
    }
  }

  return null;
}

// ─── Helpers ────────────────────────────────────────────────────────────

function escapeQuotes(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

function formatValue(value: any, field: any): string {
  if (value == null) return "''";

  if (typeof value === 'object') {
    if (
      field?.fieldSubType &&
      [
        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET,
        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET_PAGINATED,
      ].includes(field.fieldSubType as EIremboFormlyFieldSubTypes)
    ) {
      return `'${escapeQuotes(String(value.value))}'`;
    }
    return JSON.stringify(value);
  }
  if (typeof value === 'boolean') return value.toString();
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    return value.trim();
  }
  return typeof value === 'string' ? `'${escapeQuotes(value)}'` : String(value);
}

function formatRuleValue(
  value: string | undefined,
  valueType?: string
): string {
  if (value == null || value === '') return "''";
  if (valueType === 'number') {
    return isNaN(Number(value)) ? '0' : value;
  }
  return `'${escapeQuotes(value)}'`;
}

// ─── Expression parser (reverse of generator) ──────────────────────────

const REVERSE_COMPARATOR_MAP: Record<string, string> = {};
for (const [key, value] of Object.entries(COMPARATOR_MAP)) {
  REVERSE_COMPARATOR_MAP[value] = key;
}
// Backward compat: also accept loose == and != from older saved expressions
REVERSE_COMPARATOR_MAP['=='] =
  REVERSE_COMPARATOR_MAP['=='] || EServiceChangeRequestComparator.EQ;
REVERSE_COMPARATOR_MAP['!='] =
  REVERSE_COMPARATOR_MAP['!='] || EServiceChangeRequestComparator.NEQ;

interface IFieldPathInfo {
  field: IBasicFieldLabelAndDetails<TFormlyFieldKey>;
  fullPath: string;
  fullPathWithDataset?: string;
}

/**
 * Parses expression strings back into visual builder rules.
 * Returns parsed rules and any expressions that couldn't be parsed (custom entries).
 */
export const parseExpressionToRules = (
  expressions: Record<string, any>,
  availableFields: { label: string | null; value: any }[],
  targets?: IExpressionTarget[]
): { rules: IExpressionBuilderRule[]; unparsed: Record<string, string> } => {
  const effectiveTargets = targets || EXPRESSION_TARGETS;
  const rules: IExpressionBuilderRule[] = [];
  const unparsed: Record<string, string> = {};

  const fieldPaths: IFieldPathInfo[] = availableFields.map(f => {
    const detail = f.value;
    const fullPath = `${detail.rootModelPath}${
      detail.fieldRefPath ? '?.' + detail.fieldRefPath : ''
    }`;
    const isDataset =
      detail.fieldSubType &&
      [
        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET,
        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET_PAGINATED,
      ].includes(detail.fieldSubType as EIremboFormlyFieldSubTypes);
    return {
      field: detail,
      fullPath,
      fullPathWithDataset: isDataset ? `${fullPath}?.value` : undefined,
    };
  });

  // Sort by path length (longest first) for greedy matching
  fieldPaths.sort((a, b) => {
    const aLen = Math.max(
      a.fullPath.length,
      a.fullPathWithDataset?.length || 0
    );
    const bLen = Math.max(
      b.fullPath.length,
      b.fullPathWithDataset?.length || 0
    );
    return bLen - aLen;
  });

  for (const [targetKey, expr] of Object.entries(expressions)) {
    if (typeof expr !== 'string') continue;

    const target = effectiveTargets.find(t => t.value === targetKey);
    if (!target) {
      unparsed[targetKey] = expr;
      continue;
    }

    try {
      const rule = parseOneRule(targetKey, expr, fieldPaths, effectiveTargets);
      if (rule) {
        rules.push(rule);
      } else {
        unparsed[targetKey] = expr;
      }
    } catch {
      unparsed[targetKey] = expr;
    }
  }

  return { rules, unparsed };
};

function parseOneRule(
  targetProperty: string,
  expr: string,
  fieldPaths: IFieldPathInfo[],
  targets: IExpressionTarget[]
): IExpressionBuilderRule | null {
  const target = targets.find(t => t.value === targetProperty);
  const inner = expr.trim();

  // Value target → parse as ternary expression (condition ? value : default)
  if (target?.type === 'value') {
    const ternary = splitTernary(inner);
    if (!ternary) return null;

    const conditionGroups = parseConditionGroups(ternary.condition, fieldPaths);
    if (!conditionGroups || conditionGroups.length === 0) return null;

    return {
      targetProperty,
      action: 'trueWhen',
      valueWhenTrue: stripQuotes(ternary.whenTrue),
      defaultValue: stripQuotes(ternary.whenFalse),
      conditionGroups,
    };
  }

  // Boolean target → parse with optional negation wrapper
  let action: 'trueWhen' | 'falseWhen' = 'trueWhen';
  let condStr = inner;

  if (condStr.startsWith('!(') && condStr.endsWith(')')) {
    action = 'falseWhen';
    condStr = condStr.slice(2, -1).trim();
  }

  const conditionGroups = parseConditionGroups(condStr, fieldPaths);
  if (!conditionGroups || conditionGroups.length === 0) return null;

  return { targetProperty, action, conditionGroups };
}

/**
 * Parses a condition string into condition groups.
 * Splits by the lower-precedence operator at depth 0, then parses each
 * group's conditions. Parenthesized sub-expressions become separate groups.
 */
function parseConditionGroups(
  condStr: string,
  fieldPaths: IFieldPathInfo[]
): IConditionGroup[] | null {
  // Step 1: Try splitting by || at depth 0 (lower precedence)
  const orSplit = splitByOperator(condStr, '||');
  if (orSplit.tokens.length > 1) {
    return buildGroupsFromSplit(orSplit.tokens, 'OR', fieldPaths);
  }

  // Step 2: Try splitting by && at depth 0
  const andSplit = splitByOperator(condStr, '&&');
  if (andSplit.tokens.length > 1) {
    // Check if any token is a parenthesized sub-expression (contains ||)
    const hasParenGroups = andSplit.tokens.some(t => {
      const unwrapped = stripBalancedParens(t.trim());
      return unwrapped !== null && unwrapped.includes('||');
    });

    if (hasParenGroups) {
      return buildGroupsFromSplit(andSplit.tokens, 'AND', fieldPaths);
    }

    // All simple AND conditions → single group
    const conditions = parseFlatConditions(condStr, fieldPaths);
    return conditions && conditions.length > 0 ? [{ conditions }] : null;
  }

  // Step 3: Single condition or parenthesized single group
  const unwrapped = stripBalancedParens(condStr.trim());
  const toParse = unwrapped !== null ? unwrapped : condStr;
  const conditions = parseFlatConditions(toParse, fieldPaths);
  return conditions && conditions.length > 0 ? [{ conditions }] : null;
}

function buildGroupsFromSplit(
  tokens: string[],
  betweenOperator: 'AND' | 'OR',
  fieldPaths: IFieldPathInfo[]
): IConditionGroup[] | null {
  const groups: IConditionGroup[] = [];

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i].trim();
    if (!token) continue;

    // Unwrap parentheses if present
    const unwrapped = stripBalancedParens(token) || token;
    const conditions = parseFlatConditions(unwrapped, fieldPaths);
    if (!conditions || conditions.length === 0) return null;

    groups.push({
      groupOperator: i > 0 ? betweenOperator : undefined,
      conditions,
    });
  }

  return groups.length > 0 ? groups : null;
}

function parseFlatConditions(
  condStr: string,
  fieldPaths: IFieldPathInfo[]
): IExpressionBuilderCondition[] | null {
  const { tokens, operators } = splitExpression(condStr);
  const conditions: IExpressionBuilderCondition[] = [];

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i].trim();
    if (!token) continue;
    const op = i > 0 ? operators[i - 1] : undefined;
    const parsed = parseTokenGroup(token, fieldPaths, op);
    if (!parsed || parsed.length === 0) return null;
    conditions.push(...parsed);
  }

  return conditions.length > 0 ? conditions : null;
}

/**
 * Split expression by a specific operator (&& or ||) at depth 0 only.
 */
function splitByOperator(expr: string, op: '&&' | '||'): { tokens: string[] } {
  const tokens: string[] = [];
  let current = '';
  let depth = 0;
  let inString: string | null = null;

  for (let i = 0; i < expr.length; i++) {
    const ch = expr[i];
    if (inString) {
      current += ch;
      if (ch === inString && expr[i - 1] !== '\\') inString = null;
      continue;
    }
    if (ch === "'" || ch === '"' || ch === '`') {
      inString = ch;
      current += ch;
    } else if (ch === '(' || ch === '[') {
      depth++;
      current += ch;
    } else if (ch === ')' || ch === ']') {
      depth--;
      current += ch;
    } else if (depth === 0 && expr[i] === op[0] && expr[i + 1] === op[1]) {
      tokens.push(current.trim());
      current = '';
      i++; // skip second char of operator
    } else {
      current += ch;
    }
  }
  tokens.push(current.trim());
  return { tokens };
}

function splitExpression(expr: string): {
  tokens: string[];
  operators: ('AND' | 'OR')[];
} {
  const tokens: string[] = [];
  const operators: ('AND' | 'OR')[] = [];
  let current = '';
  let depth = 0;
  let inString: string | null = null;

  for (let i = 0; i < expr.length; i++) {
    const ch = expr[i];
    if (inString) {
      current += ch;
      if (ch === inString && expr[i - 1] !== '\\') inString = null;
      continue;
    }
    if (ch === "'" || ch === '"' || ch === '`') {
      inString = ch;
      current += ch;
    } else if (ch === '(' || ch === '[') {
      depth++;
      current += ch;
    } else if (ch === ')' || ch === ']') {
      depth--;
      current += ch;
    } else if (depth === 0 && ch === '&' && expr[i + 1] === '&') {
      tokens.push(current.trim());
      operators.push('AND');
      current = '';
      i++;
    } else if (depth === 0 && ch === '|' && expr[i + 1] === '|') {
      tokens.push(current.trim());
      operators.push('OR');
      current = '';
      i++;
    } else {
      current += ch;
    }
  }
  tokens.push(current.trim());
  return { tokens, operators };
}

function stripBalancedParens(token: string): string | null {
  const trimmed = token.trim();
  if (!trimmed.startsWith('(') || !trimmed.endsWith(')')) return null;
  // Check if the opening paren at 0 matches the closing paren at end
  let depth = 0;
  for (let i = 0; i < trimmed.length; i++) {
    if (trimmed[i] === '(') depth++;
    else if (trimmed[i] === ')') depth--;
    // If depth hits 0 before the end, parens are not wrapping the whole expression
    if (depth === 0 && i < trimmed.length - 1) return null;
  }
  return trimmed.slice(1, -1);
}

function parseTokenGroup(
  token: string,
  fieldPaths: IFieldPathInfo[],
  operator?: 'AND' | 'OR'
): IExpressionBuilderCondition[] | null {
  // Try direct parse first
  const direct = parseToken(token, fieldPaths, operator);
  if (direct) return [direct];

  // If wrapped in balanced parentheses, unwrap and recursively parse
  const unwrapped = stripBalancedParens(token);
  if (unwrapped !== null) {
    const { tokens: subTokens, operators: subOps } = splitExpression(unwrapped);
    const results: IExpressionBuilderCondition[] = [];
    for (let i = 0; i < subTokens.length; i++) {
      const sub = subTokens[i].trim();
      if (!sub) continue;
      // First sub-condition inherits the outer operator; subsequent ones use inner operators
      const subOp = i === 0 ? operator : subOps[i - 1];
      const subParsed = parseTokenGroup(sub, fieldPaths, subOp);
      if (!subParsed || subParsed.length === 0) return null;
      results.push(...subParsed);
    }
    return results.length > 0 ? results : null;
  }

  return null;
}

// ── Field self-reference: field?.hide, !field?.hide, field?.props?.required, etc. ──

function tryParseFieldSelfReference(
  token: string,
  operator?: 'AND' | 'OR'
): IExpressionBuilderCondition | null {
  // Matches patterns like: field?.hide, !field?.hide, field?.props?.disabled
  const match = token.match(/^(!?)field\?\.([\w?.]+)$/);
  if (!match) return null;

  const negated = match[1] === '!';
  const propPath = match[2];

  return {
    operator,
    comparedto: 'formField',
    formField: {
      key: '__self__',
      fieldRefPath: '__self__',
      label: 'This field',
    },
    formFieldProperty: {
      value: propPath,
      label: `field.${propPath}`,
      type: 'boolean',
    },
    fieldvalue: !negated,
  };
}

function parseToken(
  token: string,
  fieldPaths: IFieldPathInfo[],
  operator?: 'AND' | 'OR'
): IExpressionBuilderCondition | null {
  return (
    tryParseFieldSelfReference(token, operator) ||
    tryParseFormState(token, operator) ||
    tryParseFieldValidity(token, fieldPaths, operator) ||
    tryParseFieldProperty(token, fieldPaths, operator) ||
    tryParseFieldValue(token, fieldPaths, operator)
  );
}

// ── FormState: formState?.KEY?.fieldKey?.valuePath op value ──

function tryParseFormState(
  token: string,
  operator?: 'AND' | 'OR'
): IExpressionBuilderCondition | null {
  if (!token.startsWith('formState?.')) return null;

  // Try CONTAINS first: formState?.KEY?.path?.includes(value)
  const inclMatch = token.match(
    /^(formState\?\.[\w?.[\]]+?)\??\.includes\((.+)\)$/
  );
  if (inclMatch) {
    return parseFormStatePath(
      inclMatch[1],
      'includes',
      inclMatch[2].trim(),
      operator
    );
  }

  // Standard comparator: formState?.KEY?.path op value
  const compMatch = token.match(
    /^(formState\?\.[\w?.[\]]+?)\s*(===|!==|==|!=|>=|<=|>|<)\s*(.+)$/
  );
  if (!compMatch) return null;

  return parseFormStatePath(
    compMatch[1],
    compMatch[2],
    compMatch[3].trim(),
    operator
  );
}

function parseFormStatePath(
  fullPath: string,
  comparatorSym: string,
  rawValue: string,
  operator?: 'AND' | 'OR'
): IExpressionBuilderCondition | null {
  const pathStr = fullPath.replace('formState?.', '');
  const parts = pathStr.split('?.');
  if (parts.length < 2) return null;

  const formStateKey = parts[0];
  let fieldKey: string | undefined;
  let valuePath: string;

  // FETCHED_DATA has a fieldKey between formStateKey and valuePath
  if (
    formStateKey === EIremboFormlyFormStateKeys.FETCHED_DATA &&
    parts.length >= 3
  ) {
    fieldKey = parts[1];
    valuePath = parts.slice(2).join('?.');
  } else {
    valuePath = parts.slice(1).join('?.');
  }

  const { value, asNumber } = parseExprValue(rawValue);
  const comparator =
    comparatorSym === 'includes'
      ? EServiceChangeRequestComparator.CONTAINS
      : REVERSE_COMPARATOR_MAP[comparatorSym];

  return {
    operator,
    comparedto: 'formState',
    formStateValue: { formStateKey, fieldKey, valuePath },
    comparator,
    fieldvalue: value,
    valueAsNumber: asNumber,
  };
}

// ── Field validity: field?.parent?.parent?.form?.get('X')...valid ──

function tryParseFieldValidity(
  token: string,
  fieldPaths: IFieldPathInfo[],
  operator?: 'AND' | 'OR'
): IExpressionBuilderCondition | null {
  const match = token.match(
    /^field(?:\?\.parent)*\?\.form\?\.((?:get\??\.?\('[^']+'\)\??\.?)+)(valid|invalid)$/
  );
  if (!match) return null;

  const getChain = match[1];
  const validity = match[2];

  const parts: string[] = [];
  const regex = /get\??\.?\('([^']+)'\)/g;
  let m;
  while ((m = regex.exec(getChain)) !== null) parts.push(m[1]);
  if (parts.length === 0) return null;

  const fieldRefPath = parts.join('?.');
  const matched = fieldPaths.find(fp => fp.field.fieldRefPath === fieldRefPath);
  const lastKey = parts[parts.length - 1] || fieldRefPath;

  return {
    operator,
    comparedto: 'formField',
    formField: matched?.field || { key: lastKey, value: lastKey, fieldRefPath },
    formFieldProperty: {
      value: 'validity',
      label: "Field's Validity",
      type: 'boolean',
    },
    fieldvalue: validity === 'valid',
  };
}

// ── Field property: field?.parent...fieldGroup?.find(...)...props?.disabled ──

function tryParseFieldProperty(
  token: string,
  fieldPaths: IFieldPathInfo[],
  operator?: 'AND' | 'OR'
): IExpressionBuilderCondition | null {
  const match = token.match(
    /^field\?\.parent\?\.parent\?\.parent((?:\?\.fieldGroup\?\.find\(f => f\.key === '[^']+'\))+)\?\.props\?\.(disabled|readonly)\s*===\s*(true|false)$/
  );
  if (!match) return null;

  const findChain = match[1];
  const property = match[2];
  const value = match[3] === 'true';

  const parts: string[] = [];
  const regex = /find\(f => f\.key === '([^']+)'\)/g;
  let m;
  while ((m = regex.exec(findChain)) !== null) parts.push(m[1]);

  const fieldRefPath = parts.join('?.');
  const matched = fieldPaths.find(fp => fp.field.fieldRefPath === fieldRefPath);
  const lastKey = parts[parts.length - 1] || fieldRefPath;

  return {
    operator,
    comparedto: 'formField',
    formField: matched?.field || { key: lastKey, value: lastKey, fieldRefPath },
    formFieldProperty: {
      value: property,
      label: property === 'disabled' ? 'Disabled' : 'Readonly',
      type: 'boolean',
    },
    fieldvalue: value,
  };
}

// ── Helpers for matching fields by model path ──

function findFieldByPath(
  modelPath: string,
  fieldPaths: IFieldPathInfo[]
): IFieldPathInfo | undefined {
  const normalizedPath = modelPath.replace(/\?\./g, '.');
  const fieldName = normalizedPath.split('.').pop() || normalizedPath;

  // Try exact match on fieldRefPath first
  let match = fieldPaths.find(fp => {
    const refPath = fp.field.fieldRefPath?.replace(/\?\./g, '.');
    return refPath === normalizedPath;
  });
  if (match) return match;

  // Try suffix match (handles different section/block path representations)
  match = fieldPaths.find(fp => {
    const refPath = fp.field.fieldRefPath?.replace(/\?\./g, '.');
    return refPath?.endsWith('.' + fieldName) || refPath === fieldName;
  });
  if (match) return match;

  // Try fieldKey match
  return fieldPaths.find(fp => fp.field.fieldKey === fieldName);
}

function matchFieldByModelPath(
  modelPath: string,
  rawValue: string,
  comparatorOrKey: string,
  fieldPaths: IFieldPathInfo[],
  operator?: 'AND' | 'OR',
  rootModelPathOverride?: string
): IExpressionBuilderCondition | null {
  const fieldName = modelPath.split('?.').pop() || modelPath;
  const matchedFp = findFieldByPath(modelPath, fieldPaths);
  const { value } = parseExprValue(rawValue.trim());

  const comparator =
    comparatorOrKey === 'CONTAINS'
      ? EServiceChangeRequestComparator.CONTAINS
      : REVERSE_COMPARATOR_MAP[comparatorOrKey] || comparatorOrKey;

  // Use original field reference for dropdown matching; store path overrides separately
  // so the generator can produce the correct expression form.
  const formField = matchedFp?.field || {
    key: fieldName,
    value: fieldName,
    fieldRefPath: modelPath,
    rootModelPath: rootModelPathOverride,
  };

  return {
    operator,
    comparedto: 'formField',
    formField,
    formFieldProperty: {
      value: 'fieldvalue',
      label: 'Field value',
      type: 'unknown',
    },
    comparator,
    fieldvalue: value,
    rootModelPathOverride: rootModelPathOverride,
    fieldRefPathOverride: rootModelPathOverride ? modelPath : undefined,
  };
}

// ── FormField value: path op value ──

function tryParseFieldValue(
  token: string,
  fieldPaths: IFieldPathInfo[],
  operator?: 'AND' | 'OR'
): IExpressionBuilderCondition | null {
  for (const fp of fieldPaths) {
    // Try dataset path first (longer, more specific)
    if (fp.fullPathWithDataset && token.startsWith(fp.fullPathWithDataset)) {
      const rest = token.slice(fp.fullPathWithDataset.length);
      const cond = extractComparison(rest, fp.field, operator);
      if (cond) return cond;
    }
    if (token.startsWith(fp.fullPath)) {
      const rest = token.slice(fp.fullPath.length);
      const cond = extractComparison(rest, fp.field, operator);
      if (cond) return cond;
    }
  }

  // Fallback: field?.parent?...model?.PATH comparator value (long path with any parent depth)
  const fieldParentCompMatch = token.match(
    /^(field(?:\?\.parent)*\?\.model)\?\.([\w?.]+)\s*(===|!==|==|!=|>=|<=|>|<)\s*(.+)$/
  );
  if (fieldParentCompMatch) {
    const rootPrefix = fieldParentCompMatch[1];
    const modelPath = fieldParentCompMatch[2];
    const cond = matchFieldByModelPath(
      modelPath,
      fieldParentCompMatch[4].trim(),
      fieldParentCompMatch[3],
      fieldPaths,
      operator,
      rootPrefix
    );
    if (cond) return cond;
  }

  // Fallback: field?.parent?...model?.PATH.includes(value)
  const fieldParentIncMatch = token.match(
    /^(field(?:\?\.parent)*\?\.model)\?\.(\w+(?:\?\.\w+)*)\??\.includes\((.+)\)$/
  );
  if (fieldParentIncMatch) {
    const rootPrefix = fieldParentIncMatch[1];
    const modelPath = fieldParentIncMatch[2];
    const cond = matchFieldByModelPath(
      modelPath,
      fieldParentIncMatch[3],
      'CONTAINS',
      fieldPaths,
      operator,
      rootPrefix
    );
    if (cond) return cond;
  }

  // Fallback: field?.parent?...model?.PATH != null / == null (hasValue null-check)
  const fieldParentNullCheck = token.match(
    /^(field(?:\?\.parent)*\?\.model)\?\.([\w?.]+)\s*(!==?|===?)\s*null$/
  );
  if (fieldParentNullCheck) {
    const rootPrefix = fieldParentNullCheck[1];
    const modelPath = fieldParentNullCheck[2];
    const op = fieldParentNullCheck[3];
    const hasValue = op === '!=' || op === '!==';
    const fieldName = modelPath.split('?.').pop() || modelPath;
    const matchedFp = findFieldByPath(modelPath, fieldPaths);
    const formField = matchedFp?.field || {
      key: fieldName,
      value: fieldName,
      fieldRefPath: modelPath,
      rootModelPath: rootPrefix,
    };
    return {
      operator,
      comparedto: 'formField',
      formField,
      formFieldProperty: {
        value: 'hasValue',
        label: 'Has any value (exists)',
        type: 'boolean',
      },
      fieldvalue: hasValue,
      rootModelPathOverride: rootPrefix,
      fieldRefPathOverride: modelPath,
    };
  }

  // Fallback: field?.parent?...model?.PATH (truthy / has-value check) — including negated (legacy)
  const fieldParentTruthyMatch = token.match(
    /^(!?)(field(?:\?\.parent)*\?\.model)\?\.([\w?.]+)$/
  );
  if (fieldParentTruthyMatch) {
    const negated = fieldParentTruthyMatch[1] === '!';
    const rootPrefix = fieldParentTruthyMatch[2];
    const modelPath = fieldParentTruthyMatch[3];
    const fieldName = modelPath.split('?.').pop() || modelPath;
    const matchedFp = findFieldByPath(modelPath, fieldPaths);
    const formField = matchedFp?.field || {
      key: fieldName,
      value: fieldName,
      fieldRefPath: modelPath,
      rootModelPath: rootPrefix,
    };
    return {
      operator,
      comparedto: 'formField',
      formField,
      formFieldProperty: {
        value: 'hasValue',
        label: 'Has any value (exists)',
        type: 'boolean',
      },
      fieldvalue: !negated,
      rootModelPathOverride: rootPrefix,
      fieldRefPathOverride: modelPath,
    };
  }

  // Fallback: model?.FIELDNAME != null / == null (hasValue null-check)
  const modelNullCheck = token.match(
    /^model\?\.([\w.?]+)\s*(!==?|===?)\s*null$/
  );
  if (modelNullCheck) {
    const modelPath = modelNullCheck[1];
    const op = modelNullCheck[2];
    const hasValue = op === '!=' || op === '!==';
    const fieldName = modelPath.split('?.').pop() || modelPath;
    const matchedFp = findFieldByPath(modelPath, fieldPaths);
    const formField = matchedFp?.field || {
      key: fieldName,
      value: fieldName,
      fieldRefPath: modelPath,
      rootModelPath: 'model',
    };
    return {
      operator,
      comparedto: 'formField',
      formField,
      formFieldProperty: {
        value: 'hasValue',
        label: 'Has any value (exists)',
        type: 'boolean',
      },
      fieldvalue: hasValue,
      rootModelPathOverride: 'model',
      fieldRefPathOverride: modelPath,
    };
  }

  // Fallback: short model?.FIELDNAME comparator value
  const modelMatch = token.match(
    /^model\?\.([\w.?]+)\s*(===|!==|==|!=|>=|<=|>|<)\s*(.+)$/
  );
  if (modelMatch) {
    const modelPath = modelMatch[1];
    const cond = matchFieldByModelPath(
      modelPath,
      modelMatch[3].trim(),
      modelMatch[2],
      fieldPaths,
      operator,
      'model'
    );
    if (cond) return cond;
  }

  // Fallback: model?.FIELDNAME.includes(value)
  const modelIncMatch = token.match(
    /^model\?\.(\w+(?:\?\.\w+)*)\??\.includes\((.+)\)$/
  );
  if (modelIncMatch) {
    const modelPath = modelIncMatch[1];
    const cond = matchFieldByModelPath(
      modelPath,
      modelIncMatch[2],
      'CONTAINS',
      fieldPaths,
      operator,
      'model'
    );
    if (cond) return cond;
  }

  // Fallback: model?.FIELDNAME (truthy / has-value check) — including negated (legacy format)
  const modelTruthyMatch = token.match(/^(!?)model\?\.([\w.?]+)$/);
  if (modelTruthyMatch) {
    const negated = modelTruthyMatch[1] === '!';
    const modelPath = modelTruthyMatch[2];
    const fieldName = modelPath.split('?.').pop() || modelPath;
    const matchedFp = findFieldByPath(modelPath, fieldPaths);

    const formField = matchedFp?.field || {
      key: fieldName,
      value: fieldName,
      fieldRefPath: modelPath,
      rootModelPath: 'model',
    };

    return {
      operator,
      comparedto: 'formField',
      formField,
      formFieldProperty: {
        value: 'hasValue',
        label: 'Has any value (exists)',
        type: 'boolean',
      },
      fieldvalue: !negated,
      rootModelPathOverride: 'model',
      fieldRefPathOverride: modelPath,
    };
  }

  return null;
}

function extractComparison(
  rest: string,
  field: any,
  operator?: 'AND' | 'OR'
): IExpressionBuilderCondition | null {
  const trimmed = rest.trim();
  const fieldValueProp = {
    value: 'fieldvalue',
    label: 'Field value',
    type: 'unknown',
  };

  // .includes(value) or ?.includes(value)
  const inclMatch = trimmed.match(/^\??\.includes\((.+)\)$/);
  if (inclMatch) {
    const { value } = parseExprValue(inclMatch[1]);
    return {
      operator,
      comparedto: 'formField',
      formField: field,
      formFieldProperty: fieldValueProp,
      comparator: EServiceChangeRequestComparator.CONTAINS,
      fieldvalue: value,
    };
  }

  // "Has any value" null-check: `!= null`/`!== null` => has value (true),
  // `== null`/`=== null` => is empty (false). Must be checked BEFORE the generic
  // comparator match below, otherwise `!= null` is mis-parsed as a plain
  // "Field value" comparison against the literal null (dropping the hasValue
  // operand, so the condition renders blank on reopen).
  const nullMatch = trimmed.match(/^\s*(===?|!==?)\s*null$/);
  if (nullMatch) {
    return {
      operator,
      comparedto: 'formField',
      formField: field,
      formFieldProperty: {
        value: 'hasValue',
        label: 'Has any value (exists)',
        type: 'boolean',
      },
      fieldvalue: nullMatch[1].startsWith('!'),
    };
  }

  // comparator value
  const compMatch = trimmed.match(/^\s*(===|!==|==|!=|>=|<=|>|<)\s*(.+)$/);
  if (compMatch) {
    const { value } = parseExprValue(compMatch[2].trim());
    return {
      operator,
      comparedto: 'formField',
      formField: field,
      formFieldProperty: fieldValueProp,
      comparator: REVERSE_COMPARATOR_MAP[compMatch[1]],
      fieldvalue: value,
    };
  }

  return null;
}

function parseExprValue(raw: string): { value: any; asNumber: boolean } {
  const s = raw.trim();
  if (s === 'true') return { value: true, asNumber: false };
  if (s === 'false') return { value: false, asNumber: false };

  // Match properly paired quotes only (same quote type on both ends)
  const singleQuoteMatch = s.match(/^'((?:[^'\\]|\\.)*)'$/);
  if (singleQuoteMatch)
    return { value: singleQuoteMatch[1].replace(/\\'/g, "'"), asNumber: false };
  const doubleQuoteMatch = s.match(/^"((?:[^"\\]|\\.)*)"$/);
  if (doubleQuoteMatch)
    return { value: doubleQuoteMatch[1].replace(/\\"/g, '"'), asNumber: false };

  if (s !== '' && !isNaN(Number(s))) return { value: s, asNumber: true };

  try {
    return { value: JSON.parse(s), asNumber: false };
  } catch {
    return { value: s, asNumber: false };
  }
}

// ─── Ternary expression helpers ─────────────────────────────────────────

function splitTernary(
  expr: string
): { condition: string; whenTrue: string; whenFalse: string } | null {
  let depth = 0;
  let inString: string | null = null;
  let questionIdx = -1;
  let colonIdx = -1;

  for (let i = 0; i < expr.length; i++) {
    const ch = expr[i];
    if (inString) {
      if (ch === inString && expr[i - 1] !== '\\') inString = null;
      continue;
    }
    if (ch === "'" || ch === '"' || ch === '`') {
      inString = ch;
      continue;
    }
    if (ch === '(' || ch === '[') {
      depth++;
      continue;
    }
    if (ch === ')' || ch === ']') {
      depth--;
      continue;
    }
    // Distinguish ternary ? from optional chaining ?. and nullish coalescing ??
    if (
      depth === 0 &&
      ch === '?' &&
      expr[i + 1] !== '.' &&
      expr[i + 1] !== '?'
    ) {
      questionIdx = i;
    }
    if (depth === 0 && ch === ':' && questionIdx >= 0) {
      colonIdx = i;
      break;
    }
  }

  if (questionIdx < 0 || colonIdx < 0) return null;

  return {
    condition: expr.slice(0, questionIdx).trim(),
    whenTrue: expr.slice(questionIdx + 1, colonIdx).trim(),
    whenFalse: expr.slice(colonIdx + 1).trim(),
  };
}

function stripQuotes(s: string): string {
  const trimmed = s.trim();
  if (
    (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
    (trimmed.startsWith('"') && trimmed.endsWith('"'))
  ) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}
